import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Sparkles, ArrowRight, MessageCircle, Phone, Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function OTPLogin() {
  const [step, setStep] = useState(1); // 1=phone, 2=verify
  const [phone, setPhone] = useState("+91");
  const [channel, setChannel] = useState("whatsapp");
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [referral, setReferral] = useState("");
  const [demoCode, setDemoCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const { refreshMe } = useAuth();
  const nav = useNavigate();

  const requestOTP = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const { data } = await api.post("/auth/otp/request", { phone, channel });
      setDemoCode(data.demo_code);
      setStep(2);
      toast.success(`OTP sent via ${channel} — check screen (demo mode)`);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    }
    setBusy(false);
  };

  const verifyOTP = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      await api.post("/auth/otp/verify", { phone, code, name: name || undefined, referral_code: referral || undefined });
      await refreshMe();
      toast.success("Welcome to Vedacosmos! 🕉️");
      nav("/dashboard");
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    }
    setBusy(false);
  };

  return (
    <div className="min-h-[90vh] flex items-center justify-center px-6 py-16 relative overflow-hidden">
      <div className="absolute inset-0 starfield opacity-40 pointer-events-none" />
      <div className="relative w-full max-w-md">
        <Link to="/" className="flex items-center gap-3 justify-center mb-8">
          <Sparkles className="text-[#D4AF37]" size={20} strokeWidth={1.5} />
          <span className="font-serif text-2xl">Vedacosmos</span>
        </Link>

        <div className="bg-[#0A0C10] border border-white/10 rounded-2xl p-8 backdrop-blur-xl">
          <div className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-2">
            {step === 1 ? "OTP Login" : "Verify code"}
          </div>
          <h1 className="font-serif text-3xl mb-6">
            {step === 1 ? "Sign in with phone" : "Enter the 6-digit code"}
          </h1>

          {step === 1 && (
            <form onSubmit={requestOTP} className="space-y-4">
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-[#A0A4AB]">Phone Number</label>
                <input
                  data-testid="otp-phone-input"
                  required value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="mt-2 w-full px-4 py-3 rounded-md bg-[#12151C] border border-white/10 focus:border-[#D4AF37] outline-none"
                  placeholder="+91 98765 43210"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-[#A0A4AB] mb-2 block">Send code via</label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    data-testid="otp-channel-whatsapp"
                    onClick={() => setChannel("whatsapp")}
                    className={`py-3 rounded-md border inline-flex items-center justify-center gap-2 text-sm transition ${
                      channel === "whatsapp" ? "border-emerald-500 bg-emerald-500/10 text-emerald-400" : "border-white/10 text-[#A0A4AB]"
                    }`}
                  >
                    <MessageCircle size={14}/> WhatsApp
                  </button>
                  <button
                    type="button"
                    data-testid="otp-channel-sms"
                    onClick={() => setChannel("sms")}
                    className={`py-3 rounded-md border inline-flex items-center justify-center gap-2 text-sm transition ${
                      channel === "sms" ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]" : "border-white/10 text-[#A0A4AB]"
                    }`}
                  >
                    <Phone size={14}/> SMS
                  </button>
                </div>
              </div>
              {err && <div data-testid="otp-error" className="text-sm text-[#FF5A36]">{err}</div>}
              <button
                type="submit"
                data-testid="otp-request-btn"
                disabled={busy}
                className="w-full py-3 rounded-full bg-[#D4AF37] text-black font-medium hover:bg-[#E5C865] transition disabled:opacity-50 inline-flex items-center justify-center gap-2"
              >
                {busy ? <><Loader2 size={16} className="animate-spin"/> Sending…</> : <>Send OTP <ArrowRight size={16}/></>}
              </button>
            </form>
          )}

          {step === 2 && (
            <form onSubmit={verifyOTP} className="space-y-4">
              {demoCode && (
                <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-xs text-emerald-300">
                  <div className="uppercase tracking-widest text-[10px] mb-1">Demo mode</div>
                  Your OTP is <span className="font-serif text-xl text-emerald-400 ml-1">{demoCode}</span>
                </div>
              )}
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-[#A0A4AB]">6-digit code</label>
                <input
                  data-testid="otp-code-input"
                  required maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  className="mt-2 w-full px-4 py-3 rounded-md bg-[#12151C] border border-white/10 focus:border-[#D4AF37] outline-none text-center text-2xl tracking-[0.5em] font-serif"
                  placeholder="••••••"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-[#A0A4AB]">Your Name (new user)</label>
                <input
                  data-testid="otp-name-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="mt-2 w-full px-4 py-3 rounded-md bg-[#12151C] border border-white/10 focus:border-[#D4AF37] outline-none"
                  placeholder="Optional"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] text-[#A0A4AB]">Referral Code (optional)</label>
                <input
                  data-testid="otp-referral-input"
                  value={referral}
                  onChange={(e) => setReferral(e.target.value.toUpperCase())}
                  className="mt-2 w-full px-4 py-3 rounded-md bg-[#12151C] border border-white/10 focus:border-[#D4AF37] outline-none uppercase tracking-widest"
                  placeholder="e.g. ARJU1234"
                />
                <div className="text-[10px] text-[#A0A4AB] mt-1">Get ₹100 wallet bonus with a valid referral code.</div>
              </div>
              {err && <div className="text-sm text-[#FF5A36]">{err}</div>}
              <button
                type="submit"
                data-testid="otp-verify-btn"
                disabled={busy}
                className="w-full py-3 rounded-full bg-[#D4AF37] text-black font-medium hover:bg-[#E5C865] transition disabled:opacity-50"
              >
                {busy ? "Verifying…" : "Verify & Sign in"}
              </button>
              <button type="button" onClick={() => setStep(1)} className="w-full text-xs text-[#A0A4AB] hover:text-white">
                ← Change phone number
              </button>
            </form>
          )}

          <div className="text-sm text-[#A0A4AB] text-center mt-6">
            Prefer email? <Link to="/login" className="text-[#D4AF37]">Sign in with email</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
